import { Drawer, Radio } from 'antd';
import PropType from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectGlobalUserInfo } from 'state/global/auth/selector';
import { selectGlobalMyProfile } from 'state/global/profile/selector';
import { TRANSLATION_LANGUAGE_LIST } from 'util/translation/constant';
import {
  MEASUREMENT_FORMAT,
  MEASUREMENT_FORMAT_STORAGE_KEY
} from 'state/global/profile/util/constant';
import {
  fetchMyProfile,
  setProfileMeasurementFormat
} from 'state/global/profile/operation';
import { BaseButton } from 'components/UI/BaseButton';
import { routePaths } from 'routes/route-paths';
import { getUserName } from 'util/auth';
import { Close } from 'assets/images';
import { Link } from 'react-router-dom';
import { BaseSelect } from 'components/UI/BaseSelect';

export const Profile = (props) => {
  const { closeShowProfile, showProfile } = props;
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const globalUserInfo = useSelector((state) => selectGlobalUserInfo(state));
  const globalMyProfile = useSelector((state) => selectGlobalMyProfile(state));

  const accountDeletionUrl = useMemo(() => {
    return [
      process.env.REACT_APP_KAERCHER_PROFESSIONAL_ACCOUNT_DELETION_URL,
      '?returnUrl=',
      process.env.REACT_APP_WEB_PORTAL_URL,
      routePaths.accountDeleted
    ].join('');
  }, []);

  const deleteAccount = useCallback(() => {
    window.location.href = accountDeletionUrl;
  }, [accountDeletionUrl]);

  const onChangeLanguage = useCallback(
    (lng) => {
      i18n.changeLanguage(lng);
    },
    [i18n]
  );

  const dataFormatOptions = [
    {
      label: t('profilePanel.format.metric'),
      value: MEASUREMENT_FORMAT.METRIC
    },
    {
      label: t('profilePanel.format.imperial'),
      value: MEASUREMENT_FORMAT.IMPERIAL
    }
  ];

  useEffect(() => {
    const kaercherUserId = globalUserInfo?.identities?.[0]?.userId;
    if (kaercherUserId) {
      dispatch(fetchMyProfile(kaercherUserId));
    }
  }, [dispatch, globalUserInfo?.identities]);

  const onChangeMeasurementFormat = ({ target: { value } }) => {
    dispatch(setProfileMeasurementFormat(value));
  };

  return (
    <Drawer
      className="profile-drawer"
      title={t('profilePanel.title')}
      placement="right"
      closable
      closeIcon={<Close />}
      onClose={closeShowProfile}
      width={350}
      height={850}
      open={showProfile}>
      <div className="profile-info-wrapper">
        <p className="profile-info-heading">{t('profilePanel.username')}</p>
        <p className="profile-info">
          {getUserName(globalUserInfo?.name, globalMyProfile?.displayName)}
        </p>

        <p className="profile-info-heading">{t('profilePanel.email')}</p>
        <p className="profile-info">{globalUserInfo.email}</p>

        <p className="profile-info-heading">{t('profilePanel.phoneNumber')}</p>
        <p className="profile-info">{globalMyProfile?.phoneNumber}</p>

        <p className="profile-info-heading">{t('profilePanel.organisation')}</p>
        <p className="profile-info">{globalMyProfile?.organization}</p>

        <p className="profile-info-heading">{t('profilePanel.accountId')}</p>
        <p className="profile-info">{globalUserInfo.preferred_username}</p>

        <p className="profile-info-heading">{t('profilePanel.legalTitle')}</p>
        <p className="profile-info">
          <Link
            onClick={() => closeShowProfile(true)}
            className="profile-info-link"
            to={routePaths.dataProtection}>
            {t('profilePanel.legalLinkName')}
          </Link>
        </p>
        <p className="profile-info-heading">
          {t('profilePanel.format.header')}
        </p>
        <p className="profile-info">
          <Radio.Group
            options={dataFormatOptions}
            onChange={onChangeMeasurementFormat}
            value={globalMyProfile[MEASUREMENT_FORMAT_STORAGE_KEY]}
            optionType="button"
            buttonStyle="solid"
          />
        </p>
      </div>
      <div>
        <p className="profile-info-heading">{t('profilePanel.language')}</p>
        <p className="profile-info">{t('profilePanel.languageHint')}</p>
      </div>
      <div>
        <BaseSelect
          bordered={false}
          className="translation-switcher"
          onChange={onChangeLanguage}
          defaultValue={i18n.language}>
          {TRANSLATION_LANGUAGE_LIST.map((option) => (
            <BaseSelect.Option key={option.value} value={option.value}>
              {option.name}
            </BaseSelect.Option>
          ))}
        </BaseSelect>
      </div>

      <div className="account-deletion-wrapper">
        <BaseButton
          className="base-yellow-btn account-deletion-btn"
          onClick={deleteAccount}>
          {t('profilePanel.accountDeletionBtn')}
        </BaseButton>
      </div>
    </Drawer>
  );
};

Profile.propTypes = {
  showProfile: PropType.bool.isRequired,
  closeShowProfile: PropType.func.isRequired
};
